import React, { useState } from "react";

// Modules
import moment from "moment";
import { NestedMenuItem } from "mui-nested-menu";

// App
import { patchNode } from "../../core/postNode";
import BackButton from "./backButton";
import MediaNameEditor from "./mediaNameEditor";
import { decodeHtmlEntities, humanFileSize } from "../../utils/helpers";

// UI components
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {
  Checkbox,
  CircularProgress,
  Alert,
  FormControl,
  Box,
  Button,
  Stack,
  InputLabel,
  Typography,
  Select,
  OutlinedInput,
  MenuItem,
} from "@mui/material/";
import { Done } from "@mui/icons-material";
import { ChevronLeft } from "@mui/icons-material";

const input_font_size = 14;

const menuProps = {
  anchorOrigin: {
    vertical: "top",
    horizontal: "left",
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "right",
  },
  PaperProps: { style: { pointerEvents: "auto" } },
};

export default function SelectedDocument(props) {
  const [isError, setError] = useState(null);
  const [isPatching, setPatching] = useState(false);

  // core
  const [coreLibrary, setCoreLibrary] = useState(
    props.selectedMedia.core_library === "1" ? true : false
  );

  // category
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [selectedCategoryItem, setSelectedCategoryItem] = useState(
    props.selectedMedia
      ? {
          value: props.selectedMedia?.category?.tid
            ? props.selectedMedia?.category?.tid
            : 0,
        }
      : { value: 0 }
  );

  // category select
  const [open, setOpen] = useState(false);

  const handleCoreLibrary = (checked) => {
    setPatching(true);
    setCoreLibrary(checked ? true : false);
    syncData(selectedCategory, checked);
  };

  const syncData = (category, core_library) => {
    const data = {
      mid: props.selectedMedia.mid,
      category: category,
      core_library: core_library,
    };

    patchNode(
      `media_library/${props.selectedMedia.mid}`,
      data,
      props.user.csrf_token
    )
      .then((_response) => {
        setPatching(false);
        // props.onTimestampUpdate();
      })
      .catch((error) => {
        setPatching(false);
      });
  };

  const handleCategorySelection = (tid) => {
    setOpen(false);

    let catItem = props.categories.filter((item) => {
      return item.value === tid;
    });

    setSelectedCategoryItem({
      label: catItem[0].label,
      value: catItem[0].value,
    });

    setPatching(true);
    setSelectedCategory(parseInt(catItem[0].value, 10));
    syncData(catItem[0].value, coreLibrary);
  };

  const allCategoryValues = React.useMemo(() => {
    // only include real taxonomy IDs, skip 0 because "All Categories" is added manually
    return props.categories.map((c) => c.value);
  }, [props.categories]);

  const renderMenuItems = (parentId = null) => {
    const items = props.categories.filter(
      (item) => item.parent_target_id === parentId
    );

    return items.map((item) => {
      const children = props.categories.filter(
        (child) => child.parent_target_id === item.value
      );

      if (children.length > 0) {
        return (
          <NestedMenuItem
            key={`cat-${item.value}`}
            leftIcon={<ChevronLeft />}
            rightIcon={null}
            parentMenuOpen={open}
            label={decodeHtmlEntities(item.label)}
            MenuProps={menuProps}
            className="media-category-nested"
            onClick={() => handleCategorySelection(item.value)}
          >
            {renderMenuItems(item.value)}
          </NestedMenuItem>
        );
      }

      return (
        <MenuItem
          key={`cat-${item.value}`}
          value={item.value}
          sx={{ fontSize: input_font_size }}
          onClick={() => handleCategorySelection(item.value)}
        >
          {decodeHtmlEntities(item.label)}
        </MenuItem>
      );
    });
  };

  return (
    <>
      <Row className="mt-3 mb-3 justify-content-center">
        <Col>
          <div
            className={`crop-actions ${props.isNotForInsert ? "singular" : ""}`}
          >
            <BackButton onCancel={props.onCancel} />
            <>
              {!props.isNotForInsert && (
                <Stack direction="row" spacing={2} textAlign="center">
                  <Button
                    variant="contained"
                    color="secondary"
                    endIcon={<Done />}
                    onClick={() => {
                      props.onMediaInsert(props.selectedMedia);
                    }}
                  >
                    Select
                  </Button>
                </Stack>
              )}
            </>
          </div>
        </Col>
      </Row>
      {isError ? (
        <Row>
          <Col>
            <Alert
              variant="filled"
              severity="error"
              onClose={() => {
                setError(null);
              }}
            >
              <p>{isError}</p>
            </Alert>
          </Col>
        </Row>
      ) : (
        <Row className="selected-document">
          <Col sm={12} md={6} className="controls-container">
            <div className="text-center w-100">
              <iframe
                title={`${props.selectedMedia.name}`}
                src={`${props.selectedMedia.field_media_document}#view=fit`}
                style={{ minHeight: "400px" }}
                width="100%"
              />
              <a
                href={`${props.selectedMedia.field_media_document}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View full document
              </a>
            </div>
          </Col>

          <Col sm={12} lg={6} className={"mb-3 info-container"}>
            <div className="wrapper">
              {isPatching && (
                <Box className="info-loader">
                  <CircularProgress size={20} />
                </Box>
              )}
              <div className="form-group">
                <MediaNameEditor
                  selectedMedia={props.selectedMedia}
                  user={props.user}
                  csrf_token={props.user.csrf_token}
                />
                <Row className="mb-2 align-items-center info-row">
                  <Col xs={4} sm={3}>
                    <Typography variant="form-group-label" owner="admin">
                      MID
                    </Typography>
                  </Col>
                  <Col>
                    <Typography
                      variant="form-group-label"
                      owner="admin"
                      weight="regular"
                      color="adminText.main"
                    >
                      {props.selectedMedia.mid}
                    </Typography>
                  </Col>
                </Row>
                <Row className="mb-2 align-items-center info-row">
                  <Col xs={4} sm={3}>
                    <Typography variant="form-group-label" owner="admin">
                      Created
                    </Typography>
                  </Col>
                  <Col>
                    <Typography
                      variant="form-group-label"
                      owner="admin"
                      weight="regular"
                      color="adminText.main"
                    >
                      {moment
                        .unix(props.selectedMedia.created)
                        .format("h:mm:ssa - DD/MM/YYYY")}
                    </Typography>
                  </Col>
                </Row>
                <Row className="mb-2 align-items-center info-row">
                  <Col xs={4} sm={3}>
                    <Typography variant="form-group-label" owner="admin">
                      Author
                    </Typography>
                  </Col>
                  <Col>
                    <Typography
                      variant="form-group-label"
                      owner="admin"
                      weight="regular"
                      color="adminText.main"
                      break="break-all"
                    >
                      {props.selectedMedia.username}
                    </Typography>
                  </Col>
                </Row>
                {props.selectedMedia.filesize && (
                  <Row className="mb-2 align-items-center info-row">
                    <Col xs={4} sm={3}>
                      <Typography variant="form-group-label" owner="admin">
                        Filesize
                      </Typography>
                    </Col>
                    <Col>
                      <Typography
                        variant="form-group-label"
                        owner="admin"
                        weight="regular"
                        color="adminText.main"
                        break="break-all"
                      >
                        {humanFileSize(props.selectedMedia.filesize)}
                      </Typography>
                    </Col>
                  </Row>
                )}
                <Row className="mb-2 align-items-center info-row">
                  <Col xs={4} sm={3}>
                    <Typography variant="form-group-label" owner="admin">
                      Category
                    </Typography>
                  </Col>
                  <Col>
                    <FormControl
                      className="info-category-select"
                      size="small"
                      name="mediaCategory"
                      variant="outlined"
                      sx={{
                        width: 1,
                      }}
                    >
                      <InputLabel
                        sx={{
                          fontSize: input_font_size,
                          color: "primary.main",
                        }}
                        id="categories"
                      >
                        Category
                      </InputLabel>
                      <Select
                        size="small"
                        labelId="categories"
                        id="categories-standard"
                        defaultValue={props.categories[0]?.value}
                        value={selectedCategoryItem?.value}
                        label="Category"
                        open={open}
                        onClose={() => setOpen(false)}
                        onOpen={() => setOpen(true)}
                        renderValue={(selected) => {
                          const stringified = String(selected);

                          if (!stringified || stringified === "0")
                            return "All Categories";
                          const found = props.categories.find(
                            (c) => c.value === stringified
                          );

                          return found
                            ? decodeHtmlEntities(found.label)
                            : "All Categories";
                        }}
                        sx={{ fontSize: input_font_size }}
                        input={
                          <OutlinedInput
                            sx={{
                              fontSize: input_font_size,
                              "& .MuiOutlinedInput-notchedOutline": {
                                borderColor: "#c2ccd6",
                              },
                              "&:hover .MuiOutlinedInput-notchedOutline": {
                                borderWidth: "2px",
                                borderColor: "primary.main",
                              },
                            }}
                            label="Category"
                          />
                        }
                      >
                        {allCategoryValues.map((id) => (
                          <MenuItem
                            key={`register-${id}`}
                            value={id}
                            sx={{ display: "none" }}
                          />
                        ))}

                        {/* Visible top-level All Categories */}
                        <MenuItem
                          key="all-categories"
                          value={0}
                          sx={{ fontSize: input_font_size }}
                          onClick={() => handleCategorySelection(0)}
                        >
                          All Categories
                        </MenuItem>

                        {renderMenuItems(null)}
                      </Select>
                    </FormControl>
                  </Col>
                </Row>
                {props.permissions.access_media_promote_to_library && (
                  <Row className="mb-2 align-items-center info-row">
                    <Col xs={4} sm={3}>
                      <Typography variant="form-group-label" owner="admin">
                        Corporate library
                      </Typography>
                    </Col>
                    <Col>
                      <Checkbox
                        sx={{
                          marginLeft: "-13px",
                        }}
                        size="small"
                        id="core-lib-checkbox"
                        checked={coreLibrary}
                        onChange={(event) => {
                          handleCoreLibrary(
                            event.target.checked ? true : false
                          );
                        }}
                      />
                    </Col>
                  </Row>
                )}
              </div>
            </div>
          </Col>
        </Row>
      )}
    </>
  );
}
